import { useEffect, useState } from "react";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { useReviewerPreview } from "../component/buttons/preview-button/preview-dialog/hooks/use-reviewer-preview";

import type { Database } from "../../../__definitions__/database-definitions";
import type { UUID } from "../../../utils/uuid";

export type MomentHeaderData = Database["public"]["Functions"]["moment_header_data"]["Returns"][0];

/** The shared shell reads its display and navigation fields through the caller's permitted entrance. */
export function useMomentHeader(
  momentId: UUID,
  enabled = true,
): {
  data: MomentHeaderData | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
} {
  const reviewer = useReviewerPreview();

  const [lastHeader, setLastHeader] = useState<MomentHeaderData | null>(null);
  const result = useSupabaseLoader<MomentHeaderData | null, "public" | "reviewer">(
    async ({ supabase, account_id }) => {
      if (!enabled) return null;
      const args = { moment_id_: momentId, account_id_: account_id };
      const { data, error } = await (
        reviewer
          ? supabase.schema("reviewer").rpc("assigned_moment_header", args)
          : supabase.schema("public").rpc("moment_header_data", args)
      ).maybeSingle();

      if (error) throw error;
      return data;
    },
    [momentId, reviewer, enabled],
    reviewer ? "reviewer" : "public",
  );
  useEffect(() => {
    if (result.data?.id === momentId) setLastHeader(result.data);
  }, [result.data, momentId]);
  return {
    ...result,
    data: result.loading && lastHeader?.id === momentId ? lastHeader : result.data,
  };
}
